import * as React from "react";
import { SheetBodySkeleton } from "@/components/ui/SheetBodySkeleton";
import { useSheetSettled } from "@/hooks/use-sheet-settled";

/**
 * «¿YA HA SUBIDO LA HOJA?», repartido por contexto (29-08-2026).
 *
 * `useSheetSettled` contesta esa pregunta y lleva meses funcionando, pero cada
 * hoja que quería usarlo tenía que llamarlo ELLA y saber su propio `open`. Eso
 * deja fuera dos casos, que son justo los que peor van:
 *
 *  · La ficha que no conoce su `open` porque la monta el portal de Radix ya
 *    abierta (todo lo que cuelga de `DrawerContent`).
 *  · El contenido PROFUNDO: una hoja cuyo cuerpo es un componente que a su vez
 *    monta tres listas y cuatro tarjetas. El que sabe lo que cuesta montarse
 *    está tres niveles por debajo del que sabe si la hoja se ha posado.
 *
 * Con el valor en el contexto, la carcasa lo pone una vez y cualquiera lo lee
 * desde donde esté, sin ir pasándolo de mano en mano.
 *
 * LO QUE NO HACE, y conviene repetirlo aquí porque es la trampa: esto no
 * retrasa NINGUNA consulta. Los datos se piden al abrir, como siempre. Lo único
 * que espera es el momento de convertir la respuesta en nodos del DOM, para que
 * ese trabajo no caiga en los fotogramas en que la hoja se está moviendo. Ver el
 * razonamiento completo en `use-sheet-settled.ts`.
 */
const SheetSettledContext = React.createContext<boolean | null>(null);

export function SheetSettledProvider({
  open,
  children,
}: {
  /** `true` mientras la hoja debe estar abierta. Las carcasas montadas ya
   *  abiertas (DrawerContent) pasan `true` a secas: montarse ES abrirse. */
  open: boolean;
  children: React.ReactNode;
}) {
  const settled = useSheetSettled(open);
  return <SheetSettledContext.Provider value={settled}>{children}</SheetSettledContext.Provider>;
}

/**
 * ¿Se ha posado la hoja que me contiene?
 *
 * FUERA de una hoja devuelve `true`: una página no «sube», así que su contenido
 * no tiene nada que esperar. Así el mismo componente sirve dentro y fuera sin
 * preguntarse dónde está.
 */
export function useSheetIsSettled(): boolean {
  const value = React.useContext(SheetSettledContext);
  return value === null ? true : value;
}

/**
 * Lo caro, después de la subida; hasta entonces, su hueco.
 *
 * ```tsx
 * <DeferUntilSettled skeleton={<SheetBodySkeleton rows={3} />}>
 *   <FichaCarisima />
 * </DeferUntilSettled>
 * ```
 *
 * `loading` es para cuando además faltan datos: mantiene el hueco puesto
 * después de que la hoja se haya posado.
 */
export function DeferUntilSettled({
  skeleton,
  loading = false,
  children,
}: {
  /** El hueco. Por defecto, el cuerpo de hoja compartido. */
  skeleton?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}) {
  const settled = useSheetIsSettled();
  if (loading || !settled) {
    return <>{skeleton ?? <SheetBodySkeleton rows={4} padded={false} />}</>;
  }
  return <>{children}</>;
}
